import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isAttempt,
  type Aggregate,
  type Attempt,
  type PersonalStore,
} from '../infrastructure/indexeddb/PersonalStore';
import { hiddenStrength } from '../domain/puzzle/adaptive';
import { reviewQueue } from '../infrastructure/api/PuzzleQueue';
import { localDay, nextMidnight } from '../domain/practice/calendar';

export function sessionStats(attempts: Attempt[], since: number) {
  const current = attempts
    .filter((attempt) => attempt.startedAt >= since)
    .sort((a, b) => a.startedAt - b.startedAt || a.id.localeCompare(b.id));
  let solved = 0,
    finished = 0,
    firstTry = 0,
    streak = 0;
  for (const attempt of current) {
    if (attempt.outcome === 'solved' || attempt.outcome === 'revealed') {
      finished++;
      if (attempt.outcome === 'solved') {
        solved++;
        if (attempt.solvedFirstAttempt) firstTry++;
      }
    }
    streak = attempt.solvedFirstAttempt ? streak + 1 : 0;
  }
  return { solved, streak, firstTry: finished ? Math.round((firstTry / finished) * 100) : null };
}

/** Infer only the most recent practice block, without persisting a session boundary. */
export function recentPractice(attempts: Attempt[]) {
  const ordered = [...attempts].sort(
    (a, b) => a.startedAt - b.startedAt || a.id.localeCompare(b.id),
  );
  if (!ordered.length) return undefined;
  let recent: Attempt[] = [];
  let lastActivity = -Infinity;
  for (const attempt of ordered) {
    if (attempt.startedAt - lastActivity >= 30 * 60 * 1000) recent = [];
    recent.push(attempt);
    // A long puzzle (or overlapping tabs) is activity, not an idle gap.
    lastActivity = Math.max(lastActivity, attempt.startedAt, attempt.completedAt);
  }
  return sessionStats(recent, -Infinity);
}

/**
 * Long-term practice picture from saved attempts only (no new storage).
 * A practice day is a local calendar day with at least one finished tactic.
 * Deliberately no day streak: the app never asks you to come back (R5).
 */
export function allTimeStats(attempts: Attempt[], now = Date.now()) {
  const days = new Set<string>();
  const today = localDay(now);
  const date = new Date(now);
  const weekStart = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 6).getTime();
  const tomorrow = nextMidnight(now);
  let solved = 0,
    finished = 0,
    firstTry = 0,
    todaySolved = 0,
    week = 0;
  for (const a of attempts) {
    if (!Number.isFinite(a.completedAt) || !['solved', 'revealed'].includes(a.outcome)) continue;
    const day = localDay(a.completedAt);
    days.add(day);
    finished++;
    if (a.solvedFirstAttempt) firstTry++;
    if (a.outcome === 'solved') {
      solved++;
      if (day === today) todaySolved++;
      if (a.completedAt >= weekStart && a.completedAt < tomorrow) week++;
    }
  }
  return {
    solved,
    todaySolved,
    playedToday: days.has(today),
    firstTry: finished ? Math.round((firstTry / finished) * 100) : null,
    days: days.size,
    /** Solved in the last seven calendar days, today included. */
    week,
  };
}

/** Reads existing records after callers finish their existing saves; never writes storage. */
export function useSessionStats(store: PersonalStore) {
  const [since] = useState(() => performance.timeOrigin);
  const [history, setHistory] = useState<Attempt[]>([]);
  const [stats, setStats] = useState(() => sessionStats([], since));
  const [recent, setRecent] = useState<ReturnType<typeof recentPractice>>();
  const [allTime, setAllTime] = useState<ReturnType<typeof allTimeStats>>();
  // R5: missed puzzles due for review, and the hidden adaptive strength.
  const [review, setReview] = useState<{ due: number; upcoming: number }>();
  const [strength, setStrength] = useState<number>();
  const lifetime = useRef({ active: false, revision: 0 });
  const refresh = useCallback(async () => {
    const request = ++lifetime.current.revision;
    const [saved, aggregates] = await Promise.all([
      store.all<unknown>('attempts'),
      store.all<Aggregate>('puzzleAggregates'),
    ]);
    const attempts = saved.filter(isAttempt);
    if (lifetime.current.active && request === lifetime.current.revision) {
      setStats(sessionStats(attempts, since));
      setRecent(recentPractice(attempts));
      setAllTime(allTimeStats(attempts));
      const { due, upcoming } = reviewQueue(aggregates);
      setReview({ due, upcoming });
      setStrength(hiddenStrength(attempts));
      setHistory([...attempts].sort((a, b) => b.completedAt - a.completedAt));
    }
  }, [store, since]);
  useEffect(() => {
    const current = lifetime.current;
    current.active = true;
    void refresh();
    const visible = () => {
      if (!document.hidden) void refresh();
    };
    let midnight: ReturnType<typeof setTimeout>;
    const schedule = () => {
      midnight = setTimeout(
        () => {
          visible();
          schedule();
        },
        nextMidnight() - Date.now() + 100,
      );
    };
    schedule();
    document.addEventListener('visibilitychange', visible);
    window.addEventListener('focus', visible);
    return () => {
      clearTimeout(midnight);
      document.removeEventListener('visibilitychange', visible);
      window.removeEventListener('focus', visible);
      current.active = false;
      current.revision++;
    };
  }, [refresh]);
  return { stats, recent, allTime, review, strength, history, refresh };
}
